import React from 'react';

type StorageType = 'localStorage' | 'sessionStorage' | 'url';

/**
 * 🧠 useStateWithStorage
 *
 * Un hook React qui fonctionne comme `useState`, mais qui synchronise automatiquement
 * la valeur avec le `localStorage`, le `sessionStorage` ou l’URL.
 *
 * ### Fonctionnement :
 * - Au montage, il lit la valeur depuis le storage (si disponible),
 *   sinon il utilise la valeur initiale.
 * - À chaque mise à jour de l’état, la nouvelle valeur est sauvegardée dans le storage.
 * - Fonctionne aussi bien côté client que côté serveur (protégé pour SSR).
 * - Si plusieurs storages sont fournis, la lecture se fait dans l’ordre
 *   et l’écriture se fait dans tous les storages.
 *
 * ### Storage `url` :
 * - La valeur est stockée dans les paramètres de l’URL
 * - La clé correspond au nom du paramètre
 * - La valeur est sérialisée en JSON
 *
 * ### Exemple :
 * ```tsx
 * const [filters, setFilters] = useStateWithStorage({}, {
 *   key: 'filters',
 *   type: ['url', 'localStorage']
 * });
 * ```
 *
 * @template T Type de la valeur stockée
 * @param initialValue Valeur initiale du state
 * @param options Options de configuration :
 * - `key`: clé de stockage
 * - `transform`: fonction de transformation appliquée à la valeur lue
 * - `disabled`: désactive la persistance si `true`
 * - `type`: type(s) de storage (`localStorage`, `sessionStorage`, `url`)
 * @returns Un tuple `[value, setValue]` identique à `useState`
 */
export default function useStateWithStorage<T>(
	initialValue: T,
	options: {
		key: string;
		transform?: (values: T) => T;
		disabled?: boolean;
		type?: StorageType | StorageType[];
	},
): [T, React.Dispatch<React.SetStateAction<T>>] {
	const { key, disabled = false, type = 'localStorage', transform } = options;

	/**
	 * Normalisation du type de storage :
	 * - string -> tableau
	 * - tableau -> inchangé
	 */
	const storages = React.useMemo<StorageType[]>(
		() => (Array.isArray(type) ? type : [type]),
		[type],
	);

	const initialValueWithStorage = React.useMemo<T>(() => {
		if (typeof window === 'undefined' || disabled) {
			return initialValue;
		}

		/**
		 * Lecture dans chaque storage dans l’ordre fourni.
		 * La première valeur trouvée est utilisée.
		 */
		for (const storageType of storages) {
			try {
				if (storageType === 'url') { // 📦 URL storage
					const params = new URLSearchParams(window.location.search);
					const value = params.get(key);

					if (value !== null) {
						const parsed = JSON.parse(value) as T;
						return transform ? transform(parsed) : parsed;
					}
				} else { // 📦 Web storage
					const storageValue = window[storageType].getItem(key);

					if (storageValue !== null) {
						const parsed = JSON.parse(storageValue) as T;
						return transform ? transform(parsed) : parsed;
					}
				}
			} catch (err) {
				console.warn(
					`useStateWithStorage: unable to read from ${storageType}`,
					err,
				);
			}
		}

		return initialValue;
	}, [key, storages, disabled, initialValue, transform]);

	const [value, setValue] = React.useState<T>(initialValueWithStorage);

	React.useEffect(() => {
		if (typeof window === 'undefined' || disabled) {
			return;
		}

		/**
		 * Écriture de la valeur dans tous les storages fournis
		 */
		for (const storageType of storages) {
			try {
				if (storageType === 'url') { // 📦 URL storage
					const params = new URLSearchParams(window.location.search);

					params.set(key, JSON.stringify(value));

					const newUrl =
						window.location.pathname +
						'?' +
						params.toString() +
						window.location.hash;

					window.history.replaceState(null, '', newUrl);
				} else { // 📦 Web storage
					window[storageType].setItem(key, JSON.stringify(value));
				}
			} catch (err) {
				console.warn(
					`useStateWithStorage: unable to write to ${storageType}`,
					err,
				);
			}
		}
	}, [value, key, storages, disabled]);

	return [value, setValue];
}
